import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import './RichTextEditor.css';

const EDITOR_COLORS = [
  '#1e293b', '#6366f1', '#ef4444', '#16a34a',
  '#f59e0b', '#0ea5e9', '#ec4899', '#64748b'
];

function ToolBtn({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      className={`rte-btn${active ? ' rte-btn--active' : ''}`}
    >
      {children}
    </button>
  );
}

const RichTextEditor = forwardRef(function RichTextEditor(
  { initialContent = '', onUpdate, readOnly = false },
  ref
) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: initialContent,
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'rte-content',
      },
    },
    onUpdate: ({ editor: e }) => {
      onUpdate?.({ html: e.getHTML(), isEmpty: e.isEmpty });
    },
  });

  // Sincronizar editabilidad cuando readOnly cambia en un editor ya montado
  // (necesario para campos como Brief que siempre están en el DOM)
  useEffect(() => {
    if (editor && editor.isEditable === readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() ?? '',
    get isEmpty() { return editor?.isEmpty ?? true; },
    clear: () => editor?.commands.clearContent(),
    setContent: (html) => editor?.commands.setContent(html),
  }), [editor]);

  if (!editor) return null;

  if (readOnly) {
    return (
      <div className="rte-readonly">
        <EditorContent editor={editor} />
      </div>
    );
  }

  return (
    <div className="rte-wrapper">
      {/* Toolbar */}
      <div className="rte-toolbar">
        {/* Deshacer / Rehacer */}
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Deshacer">
          <i className="bi bi-arrow-counterclockwise" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Rehacer">
          <i className="bi bi-arrow-clockwise" />
        </ToolBtn>

        <div className="rte-divider" />

        {/* Formato de texto */}
        <ToolBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrita">
          <i className="bi bi-type-bold" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Cursiva">
          <i className="bi bi-type-italic" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Subrayado">
          <i className="bi bi-type-underline" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Tachado">
          <i className="bi bi-type-strikethrough" />
        </ToolBtn>

        <div className="rte-divider" />

        {/* Listas */}
        <ToolBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista con viñetas">
          <i className="bi bi-list-ul" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada">
          <i className="bi bi-list-ol" />
        </ToolBtn>
        <ToolBtn active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Cita">
          <i className="bi bi-chat-square-quote" />
        </ToolBtn>

        <div className="rte-divider" />

        {/* Colores de fuente */}
        <div className="rte-colors">
          {EDITOR_COLORS.map(c => (
            <button
              key={c}
              type="button"
              className="rte-color-dot"
              style={{ background: c }}
              title={`Color ${c}`}
              onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setColor(c).run(); }}
            />
          ))}
          <button
            type="button"
            className="rte-color-dot rte-color-reset"
            title="Sin color"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetColor().run(); }}
          >
            <i className="bi bi-x" style={{ fontSize: '8px', color: '#9ca3af' }} />
          </button>
        </div>

        <div className="rte-divider" />

        {/* Resaltadores */}
        <ToolBtn
          active={editor.isActive('highlight', { color: '#fef08a' })}
          onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()}
          title="Resaltar amarillo"
        >
          <i className="bi bi-highlighter" style={{ color: '#ca8a04' }} />
        </ToolBtn>
        <ToolBtn
          active={editor.isActive('highlight', { color: '#bbf7d0' })}
          onClick={() => editor.chain().focus().toggleHighlight({ color: '#bbf7d0' }).run()}
          title="Resaltar verde"
        >
          <i className="bi bi-highlighter" style={{ color: '#16a34a' }} />
        </ToolBtn>
        <ToolBtn
          active={editor.isActive('highlight', { color: '#fecaca' })}
          onClick={() => editor.chain().focus().toggleHighlight({ color: '#fecaca' }).run()}
          title="Resaltar rojo"
        >
          <i className="bi bi-highlighter" style={{ color: '#dc2626' }} />
        </ToolBtn>
        <ToolBtn
          onClick={() => editor.chain().focus().unsetHighlight().run()}
          title="Quitar resaltado"
        >
          <i className="bi bi-eraser" style={{ color: '#9ca3af' }} />
        </ToolBtn>

        <div className="rte-divider" />

        {/* Limpiar formato */}
        <ToolBtn
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          title="Limpiar formato"
        >
          <i className="bi bi-slash-circle" />
        </ToolBtn>
      </div>

      {/* Área de texto */}
      <EditorContent editor={editor} />
    </div>
  );
});

export default RichTextEditor;
